import React, { useCallback, useMemo } from 'react';
import isNil from 'lodash/isNil';
import SharedCarouselButton from '../components/SharedCarouselButton';
import {
  SettingsProps,
  SettingsReturnProps,
  StyledButtonProps,
} from '../types';

const PRODUCTS_PER_ROW = 5;

export const StyledNextButton: React.FC<StyledButtonProps> = ({
  isBig,
  onClick,
  currentSlide = 0,
  slideCount = PRODUCTS_PER_ROW,
  handleTracking,
  isExtendedHomepageCarousel,
  extendedPaginationEnabled,
  position = 'right',
}) => {
  const handleClick = useCallback(() => {
    if (onClick) {
      onClick();
    }

    if (handleTracking) {
      handleTracking();
    }
  }, [onClick, handleTracking]);

  const isDisabled = useMemo(() => {
    if (isExtendedHomepageCarousel && !isNil(extendedPaginationEnabled)) {
      return !extendedPaginationEnabled;
    }

    return currentSlide >= slideCount - PRODUCTS_PER_ROW;
  }, [extendedPaginationEnabled, isExtendedHomepageCarousel, currentSlide]);

  return (
    <SharedCarouselButton
      isExtendedHomepageCarousel={isExtendedHomepageCarousel}
      onClick={handleClick}
      isDisabled={isDisabled}
      isBig={isBig}
      position={position}
    />
  );
};

export const StyledPrevButton: React.FC<StyledButtonProps> = ({
  isBig,
  onClick,
  currentSlide = 0,
  handleTracking,
  isExtendedHomepageCarousel,
  extendedPaginationEnabled,
  position = 'left',
}) => {
  const handleClick = useCallback(() => {
    if (onClick) {
      onClick();
    }

    if (handleTracking) {
      handleTracking();
    }
  }, [onClick, handleTracking]);

  const isDisabled = useMemo(() => {
    if (isExtendedHomepageCarousel && !isNil(extendedPaginationEnabled)) {
      return !extendedPaginationEnabled;
    }

    return currentSlide === 0;
  }, [extendedPaginationEnabled, isExtendedHomepageCarousel, currentSlide]);

  return (
    <SharedCarouselButton
      isExtendedHomepageCarousel={isExtendedHomepageCarousel}
      onClick={handleClick}
      isDisabled={isDisabled}
      isBig={isBig}
      position={position}
    />
  );
};

const useCarouselSettings = ({
  handleTracking,
  setActiveSlide,
  slidesToShow = PRODUCTS_PER_ROW,
  slidesToScroll = PRODUCTS_PER_ROW,
  isBigButtons = false,
}: SettingsProps = {}): SettingsReturnProps =>
  useMemo(
    () => ({
      dots: false,
      infinite: false,
      speed: 500,
      swipe: false,
      slidesToShow,
      slidesToScroll,
      beforeChange: (_currentSlide, nextSlide) =>
        setActiveSlide && setActiveSlide(nextSlide),
      prevArrow: (
        <StyledPrevButton
          handleTracking={handleTracking}
          isBig={isBigButtons}
          position="left"
        />
      ),
      nextArrow: (
        <StyledNextButton
          handleTracking={handleTracking}
          isBig={isBigButtons}
          position="right"
        />
      ),
    }),
    [handleTracking, slidesToShow, slidesToScroll],
  );

export default useCarouselSettings;
